import { useState, useEffect } from 'react';
import { Settings, Save, Loader2, Info, Dumbbell, Clock, User, TrendingUp } from 'lucide-react';
import { aiApi } from '@/services/api';
import type { AiWorkoutSettingsData } from '@/services/api';
import { Card, Button } from '@/components/common';
import { useUIStore } from '@/store/uiStore';

const INTENSITY_OPTIONS = [
  { value: 'conservative', label: 'Konzervatív', desc: 'Max 2-5% emelés, biztonságos haladás' },
  { value: 'moderate', label: 'Mérsékelt', desc: 'Max 5-10% emelés (ajánlott)' },
  { value: 'aggressive', label: 'Agresszív', desc: 'Max 10-15% emelés, tapasztalt edzőknek' },
];

const REST_TIME_OPTIONS = [
  { value: 'short', label: 'Rövid (30-60 mp)', desc: 'Intenzív edzés' },
  { value: 'medium', label: 'Közepes (60-90 mp)', desc: 'Általános' },
  { value: 'long', label: 'Hosszú (90-180 mp)', desc: 'Erőfokozás, nehéz súlyok' },
];

const EXPERIENCE_OPTIONS = [
  { value: 'beginner', label: 'Kezdő', desc: 'Egyszerűbb gyakorlatok, kisebb súlyok' },
  { value: 'intermediate', label: 'Haladó', desc: 'Normál progresszió' },
  { value: 'advanced', label: 'Profi', desc: 'Nehezebb súlyok, összetettebb technikák' },
];

const AiSettings = () => {
  const showToast = useUIStore((s) => s.showToast);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [intensity, setIntensity] = useState('moderate');
  const [maxWeightPercent, setMaxWeightPercent] = useState(5);
  const [difficultyAwareness, setDifficultyAwareness] = useState(true);
  const [restTime, setRestTime] = useState('medium');
  const [experience, setExperience] = useState('intermediate');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await aiApi.getWorkoutSettings();
        if (res.success && res.data?.settings) {
          const s = res.data.settings;
          setIntensity(s.intensity);
          setMaxWeightPercent(s.maxWeightIncreasePercent);
          setDifficultyAwareness(s.difficultyAwareness);
          setRestTime(s.restTimePreference);
          setExperience(s.trainingExperience);
        }
      } catch {
        showToast('Nem sikerült betölteni a beállításokat', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showToast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await aiApi.updateWorkoutSettings({
        intensity,
        maxWeightIncreasePercent: maxWeightPercent,
        difficultyAwareness,
        restTimePreference: restTime,
        trainingExperience: experience,
      } as AiWorkoutSettingsData);
      if (res.success) {
        showToast('Beállítások mentve!', 'success');
      } else {
        showToast('Mentési hiba', 'error');
      }
    } catch {
      showToast('Nem sikerült menteni a beállításokat', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Settings className="w-7 h-7 text-primary-600" />
          AI edzés beállítások
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Ezek a beállítások automatikusan érvényesülnek minden AI edzésterv generálásnál
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Intensity */}
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              Progressziós intenzitás
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Mennyire agresszíven emelje az AI a súlyokat az előző edzéshez képest
            </p>
            <div className="space-y-2">
              {INTENSITY_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                    intensity === opt.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="intensity"
                    value={opt.value}
                    checked={intensity === opt.value}
                    onChange={(e) => setIntensity(e.target.value)}
                    className="mt-1 accent-primary-600"
                  />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </Card>

          {/* Max weight increase */}
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-primary-600" />
              Maximális súlyemelés korlát
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Az AI nem javasolhat ennél nagyobb százalékos emelést egy edzésről a másikra
            </p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={1}
                max={20}
                value={maxWeightPercent}
                onChange={(e) => setMaxWeightPercent(Number(e.target.value))}
                className="flex-1 accent-primary-600"
              />
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-1.5 min-w-[70px] justify-center">
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{maxWeightPercent}</span>
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Pl. ha az előző edzésen 40kg volt, max {Math.round(40 * (1 + maxWeightPercent / 100))}kg javasolt
            </p>
          </Card>

          {/* Difficulty awareness */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Nehézségi szint figyelembevétele
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Ha az utolsó edzésen a nehézség magas volt (4-5/5), az AI ne emelje a súlyt
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={difficultyAwareness}
                  onChange={(e) => setDifficultyAwareness(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-600 peer-checked:bg-primary-600" />
              </label>
            </div>
          </Card>

          {/* Rest time */}
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary-600" />
              Pihenőidő preferencia
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Sorozatok közötti javasolt pihenőidő
            </p>
            <div className="flex flex-wrap gap-2">
              {REST_TIME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRestTime(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    restTime === opt.value
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Experience */}
          <Card>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
              <User className="w-5 h-5 text-primary-600" />
              Tapasztalati szint
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Az AI ehhez igazítja a gyakorlatok nehézségét és komplexitását
            </p>
            <div className="flex flex-wrap gap-2">
              {EXPERIENCE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setExperience(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    experience === opt.value
                      ? 'bg-primary-600 text-white dark:bg-primary-500'
                      : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Card>

          <Button
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            icon={<Save className="w-5 h-5" />}
            size="lg"
            className="w-full sm:w-auto"
          >
            Beállítások mentése
          </Button>
        </div>

        {/* Info panel */}
        <div>
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 sticky top-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Segítség
            </h3>
            <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">Miért emel vadul az AI?</p>
                <p>
                  Alapértelmezetten az AI progresszív túlterhelés elvén dolgozik.
                  Ha túl gyorsan emel, állítsd <strong>Konzervatívra</strong> az intenzitást
                  és csökkentsd a max emelés %-ot.
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">Nehézségi szint</p>
                <p>
                  Ha bekapcsolod, az AI figyelembe veszi az edzés közben rögzített értékeléseket.
                  Ha 4-5/5 nehézséget jelöltél, az AI nem emel tovább.
                </p>
              </div>
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-200 mb-1">Tapasztalati szint</p>
                <p>
                  <strong>Kezdő:</strong> egyszerűbb gyakorlatok, több ismétlés<br />
                  <strong>Haladó:</strong> normál edzés<br />
                  <strong>Profi:</strong> nehezebb, összetettebb gyakorlatok
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AiSettings;
